import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Lookup, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import { useApi, type TeamMember, type TeamRosterMember } from '../app/api.js'
import { Loading, messageFor } from './feedback.js'

/**
 * Who is on a job (docs/05 §Project Team).
 *
 * Somebody joins either on their own — a person and the role they hold here —
 * or with a whole team. The second is the one that matters in practice: adding
 * the purchase team a person at a time is how a project ends up with four of
 * the five people who should be on it, and nobody notices until an approval
 * sits waiting for the one who was missed.
 *
 * A team is copied in, not linked. Teams are reorganised, and who was on a job
 * last March should not change because the team was rearranged in June — but
 * each row still remembers the team it arrived with, which is what the first
 * column shows.
 */
export interface ProjectTeamProps {
  readonly projectId: string
  /** The form embeds it inside its own page; the Drive tab gives it a panel. */
  readonly title?: string
}

type Adding =
  | { readonly how: 'INDIVIDUAL'; readonly employeeId: string; readonly roleId: string }
  | { readonly how: 'TEAM'; readonly teamId: string }

export const ProjectTeam = ({ projectId, title = 'Who is on this job' }: ProjectTeamProps) => {
  const api = useApi()
  const client = useQueryClient()
  const team = useQuery({
    queryKey: ['drive', 'team', api.companyId, projectId],
    queryFn: () => api.projectTeam(projectId),
    enabled: projectId !== '',
  })
  const roles = useQuery({
    queryKey: ['master-list', api.companyId, 'project_role'],
    queryFn: () => api.masterList('project_role', { pageSize: '100' }),
  })

  const [adding, setAdding] = useState<Adding | null>(null)
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['drive', 'team', api.companyId, projectId] })
  }

  const add = useMutation({
    mutationFn: () => {
      if (!adding) throw new Error('Nothing to add.')
      return adding.how === 'TEAM'
        ? api.addTeamToProject(projectId, adding.teamId)
        : api.addToTeam(projectId, {
            employeeId: adding.employeeId,
            ...(adding.roleId ? { projectRoleId: adding.roleId } : {}),
          })
    },
    onSuccess: () => { setError(null); setAdding(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (memberId: string) => api.removeFromTeam(projectId, memberId),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const columns: ReadonlyArray<ColumnDef<TeamMember>> = [
    {
      key: 'roleName',
      header: 'Project role / team',
      width: 220,
      // docs/05 shows one column for both: a person added on their own reads as
      // their role, a person who came in with a team reads as the team.
      render: (r) => r.roleName ?? r.teamName ?? '—',
    },
    { key: 'name', header: 'Assigned to' },
    { key: 'mobile', header: 'Contact no', width: 160, render: (r) => r.mobile ?? '—' },
    { key: 'designation', header: 'Designation', width: 180, render: (r) => r.designation ?? '—' },
    { key: 'email', header: 'Email', width: 230, render: (r) => r.email ?? '—' },
    {
      key: 'notify',
      header: 'Copied on email',
      type: 'bool',
      width: 140,
      render: (r) => (r.notify ? 'Yes' : 'No'),
    },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (row) => <Button kind="ghost" onClick={() => remove.mutate(row.id)}>Remove</Button>,
    },
  ]

  const teamsOnThisJob = [...new Map(
    (team.data ?? [])
      .filter((m): m is TeamMember & { teamId: string; teamName: string } =>
        m.teamId !== null && m.teamName !== null)
      .map((m) => [m.teamId, m.teamName] as const),
  )]

  return (
    <Panel
      title={title}
      aside={
        <Button
          kind="primary"
          onClick={() => setAdding({ how: 'INDIVIDUAL', employeeId: '', roleId: '' })}
        >
          Add member
        </Button>
      }
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      <p className="helper">
        Being on the team is not the same as being allowed to see the job's documents — that is
        set per person under Administration → People. Everyone here who asks to be is copied on
        the emails this project sends out.
      </p>
      {team.isLoading ? <Loading /> : null}
      <Table
        columns={columns}
        rows={team.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nobody on the team yet" body={['Add the people working on this job.']} />}
      />

      {teamsOnThisJob.length > 0 ? (
        <p className="helper" style={{ marginTop: 12 }}>
          Teams on this job:{' '}
          {teamsOnThisJob.map(([id, name], i) => (
            <span key={id}>
              {i > 0 ? ' · ' : ''}
              <button type="button" className="link-button" onClick={() => setViewing({ id, name })}>
                {name}
              </button>
            </span>
          ))}
        </p>
      ) : null}

      {adding ? (
        <Modal
          title="Add to the team"
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={
                  add.isPending ||
                  (adding.how === 'TEAM' ? adding.teamId === '' : adding.employeeId === '')
                }
                onClick={() => add.mutate()}
              >
                {adding.how === 'TEAM' ? 'Add everybody in it' : 'Add'}
              </Button>
            </>
          }
          onClose={() => setAdding(null)}
        >
          <Field label="Add individual or team?" required>
            <Select
              value={adding.how}
              onChange={(how) =>
                setAdding(how === 'TEAM'
                  ? { how: 'TEAM', teamId: '' }
                  : { how: 'INDIVIDUAL', employeeId: '', roleId: '' })
              }
              options={[
                { value: 'INDIVIDUAL', label: 'Individual' },
                { value: 'TEAM', label: 'Team' },
              ]}
            />
          </Field>

          {adding.how === 'TEAM' ? (
            <>
              <Field label="Team" required>
                <Lookup
                  entity="team"
                  value={adding.teamId}
                  onChange={(teamId) => setAdding({ how: 'TEAM', teamId })}
                />
              </Field>
              {adding.teamId ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setViewing({ id: adding.teamId, name: 'Team details' })}
                >
                  View members
                </button>
              ) : null}
              <p className="helper" style={{ marginTop: 12 }}>
                Everybody in the team is added with the role they hold in it. Somebody already
                here keeps the role they were given on this job.
              </p>
            </>
          ) : (
            <>
              <Field label="Assigned to" required>
                <Lookup
                  entity="employee"
                  value={adding.employeeId}
                  onChange={(employeeId) => setAdding({ ...adding, employeeId })}
                />
              </Field>
              <Field label="Project role">
                <Select
                  value={adding.roleId}
                  onChange={(roleId) => setAdding({ ...adding, roleId })}
                  placeholder="No particular role"
                  options={(roles.data?.rows ?? []).map((r) => ({
                    value: String(r['id']), label: String(r['name'] ?? ''),
                  }))}
                />
              </Field>
            </>
          )}
        </Modal>
      ) : null}

      {viewing ? (
        <TeamDetails teamId={viewing.id} name={viewing.name} onClose={() => setViewing(null)} />
      ) : null}
    </Panel>
  )
}

/**
 * Team Details (docs/05 §Project Team — `View members`).
 *
 * Read what is in a team before putting it on a job, and put somebody in it
 * from here: a team nobody can edit without leaving what they were doing is a
 * team that stays out of date.
 */
const TeamDetails = ({ teamId, name, onClose }: {
  teamId: string
  name: string
  onClose: () => void
}) => {
  const api = useApi()
  const client = useQueryClient()
  const roster = useQuery({
    queryKey: ['team-roster', api.companyId, teamId],
    queryFn: () => api.teamRoster(teamId),
  })
  const roles = useQuery({
    queryKey: ['master-list', api.companyId, 'project_role'],
    queryFn: () => api.masterList('project_role', { pageSize: '100' }),
  })

  const [adding, setAdding] = useState<{ employeeId: string; roleId: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['team-roster', api.companyId, teamId] })
  }

  const add = useMutation({
    mutationFn: () => api.addToRoster(teamId, {
      employeeId: adding?.employeeId ?? '',
      ...(adding?.roleId ? { projectRoleId: adding.roleId } : {}),
    }),
    onSuccess: () => { setError(null); setAdding(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (memberId: string) => api.removeFromRoster(teamId, memberId),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const columns: ReadonlyArray<ColumnDef<TeamRosterMember>> = [
    { key: 'roleName', header: 'Project role', width: 220, render: (r) => r.roleName ?? '—' },
    { key: 'name', header: 'Name' },
    { key: 'mobile', header: 'Contact no', width: 170, render: (r) => r.mobile ?? '—' },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (row) => <Button kind="ghost" onClick={() => remove.mutate(row.id)}>Remove</Button>,
    },
  ]

  return (
    <Modal
      title={`Team details — ${name}`}
      wide
      actions={<Button kind="primary" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      {roster.isLoading ? <Loading /> : null}
      <Table
        columns={columns}
        rows={roster.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nobody in this team" body={['Put somebody in it before using it on a job.']} />}
      />

      {adding ? (
        <>
          <Field label="Person" required>
            <Lookup
              entity="employee"
              value={adding.employeeId}
              onChange={(employeeId) => setAdding({ ...adding, employeeId })}
            />
          </Field>
          <Field label="Project role">
            <Select
              value={adding.roleId}
              onChange={(roleId) => setAdding({ ...adding, roleId })}
              placeholder="No particular role"
              options={(roles.data?.rows ?? []).map((r) => ({
                value: String(r['id']), label: String(r['name'] ?? ''),
              }))}
            />
          </Field>
          <div style={{ marginTop: 12 }}>
            <Button
              kind="primary"
              disabled={adding.employeeId === '' || add.isPending}
              onClick={() => add.mutate()}
            >
              Put them in the team
            </Button>{' '}
            <Button kind="ghost" onClick={() => setAdding(null)}>Cancel</Button>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 12 }}>
          <Button kind="ghost" onClick={() => setAdding({ employeeId: '', roleId: '' })}>
            Add somebody to this team
          </Button>
        </div>
      )}
    </Modal>
  )
}
